import { useState, useEffect, useCallback } from "react";
import {
  Gift, Sprout, Share2, Users, HeartHandshake, ShieldCheck, TrendingUp,
  BookOpen, Compass, Feather, MessagesSquare, HandHeart, Wheat,
  Target, Sparkles, X, Trash2, Check, Megaphone, Copy, Crown, ArrowUpRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { PanelLoading } from "../../components/SmartImage";
import { toast } from "sonner";

const ICONS = {
  Gift, Sprout, Share2, Users, HeartHandshake, ShieldCheck, TrendingUp,
  BookOpen, Compass, Feather, MessagesSquare, HandHeart, Wheat,
};

const GROUP_ACCENT = {
  "Generosity": "text-gold border-gold/40",
  "Stewardship": "text-emerald-400 border-emerald-400/40",
  "Discipleship": "text-sky-400 border-sky-400/40",
  "Brotherhood": "text-violet-400 border-violet-400/40",
  "Sowing & Reaping": "text-amber-400 border-amber-400/40",
};

export function IncreaseTab() {
  const [data, setData] = useState(null);
  const [goalModal, setGoalModal] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [view, setView] = useState("metrics");

  const load = useCallback(() => api.get("/admin/increase").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <PanelLoading label="Reading the increase" />;

  return (
    <div data-testid="increase-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="overline">// KINGDOM INCREASE — health of the whole ecosystem, by Kairos lenses</div>
        <div className="flex gap-px bg-borderGold">
          <button data-testid="increase-view-metrics" onClick={() => setView("metrics")}
            className={`bg-void px-4 py-2 text-xs font-mono uppercase tracking-widest ${view === "metrics" ? "text-gold" : "text-textMuted hover:text-cream"}`}>Metrics</button>
          <button data-testid="increase-view-teachings" onClick={() => setView("teachings")}
            className={`bg-void px-4 py-2 text-xs font-mono uppercase tracking-widest ${view === "teachings" ? "text-gold" : "text-textMuted hover:text-cream"}`}>Teachings</button>
        </div>
      </div>

      {view === "teachings" ? (
        <TeachingsView />
      ) : (
        <>
          {/* Kairos milestone prompts */}
          {data.prompts.length > 0 && (
            <div className="mb-8" data-testid="kairos-prompts">
              <div className="overline mb-3 text-gold">// KAIROS MILESTONES — a goal was reached. Tell the story, capture the lesson.</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.prompts.map((p) => (
                  <button key={p.key} data-testid={`milestone-${p.key}`} onClick={() => setMilestone(p)}
                    className="panel p-4 text-left hover:border-gold transition-colors group">
                    <div className="flex items-center gap-2 text-gold">
                      <Crown className="w-4 h-4" />
                      <span className="text-sm font-display">{p.label}</span>
                      <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-xs text-textDim mt-2">Reached <span className="text-cream">{p.value}</span> (goal {p.target})</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Metric groups */}
          {data.groups.map((group) => (
            <div key={group} className="mb-8">
              <div className={`overline mb-3 ${(GROUP_ACCENT[group] || "").split(" ")[0]}`}>// {group}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.metrics.filter((m) => m.group === group).map((m) => (
                  <MetricCard key={m.key} m={m} onGoal={() => setGoalModal(m)} />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {goalModal && <GoalModal m={goalModal} onClose={() => setGoalModal(null)} onSaved={() => { setGoalModal(null); load(); }} />}
      {milestone && <MilestoneModal p={milestone} onClose={() => setMilestone(null)} onDone={() => { setMilestone(null); load(); }} />}
    </div>
  );
}

function MetricCard({ m, onGoal }) {
  const Icon = ICONS[m.icon] || Target;
  const accent = (GROUP_ACCENT[m.group] || "text-gold border-gold/40");
  const g = m.goal;
  return (
    <div data-testid={`metric-${m.key}`} className={`panel p-5 border-l-2 ${accent.split(" ")[1]}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-1 ${accent.split(" ")[0]}`} />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-textDim uppercase tracking-widest font-mono">{m.label}</div>
          <div className="font-display text-4xl text-cream mt-1">{m.display}</div>
        </div>
        <button data-testid={`set-goal-${m.key}`} onClick={onGoal} className="btn-ghost text-xs !py-1 !px-2" title="Set a goal">
          <Target className="w-3 h-3" />
        </button>
      </div>

      {g && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-textDim">Goal {g.display_target}{g.by_date ? ` · by ${g.by_date}` : ""}</span>
            <span className={g.reached ? "text-gold" : "text-textMuted"}>{g.progress}%</span>
          </div>
          <div className="h-1.5 bg-surface border border-borderGold overflow-hidden">
            <div className={`h-full ${g.reached ? "bg-gold" : "bg-emerald-400/60"}`} style={{ width: `${g.progress}%` }} />
          </div>
          {g.reached && <div className="text-[10px] text-gold mt-1 font-mono uppercase tracking-widest">{g.celebrated_at ? "Celebrated" : "Milestone — celebrate it"}</div>}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-borderGold">
        <div className="overline mb-1.5 !text-[9px]">// Levers</div>
        <ul className="space-y-1">
          {m.levers.map((l, i) => (
            <li key={i} className="text-xs text-textMuted flex gap-2"><span className={accent.split(" ")[0]}>·</span>{l}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function GoalModal({ m, onClose, onSaved }) {
  const [target, setTarget] = useState(m.goal?.target ?? "");
  const [byDate, setByDate] = useState(m.goal?.by_date ?? "");

  const save = async () => {
    if (!target || Number(target) <= 0) return toast.error("Set a target greater than zero");
    try {
      await api.put(`/admin/increase/goals/${m.key}`, { target: Number(target), by_date: byDate || null });
      toast.success("Goal set"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save goal"); }
  };
  const clear = async () => {
    await api.delete(`/admin/increase/goals/${m.key}`); toast.success("Goal cleared"); onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-void/80 backdrop-blur flex items-start justify-center overflow-y-auto py-16 px-4" onClick={onClose}>
      <div className="panel p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="goal-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="overline text-gold">// SET A GOAL — {m.label}</div>
          <button onClick={onClose}><X className="w-5 h-5 text-textMuted hover:text-cream" /></button>
        </div>
        <p className="text-sm text-textMuted mb-4">Currently at <span className="text-cream">{m.display}</span>. Set the marker faith is reaching for.</p>
        <div className="space-y-4">
          <div>
            <label className="overline">// Target ({m.unit})</label>
            <input data-testid="goal-target" type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 100" />
          </div>
          <div>
            <label className="overline">// By season (optional)</label>
            <input data-testid="goal-bydate" type="date" value={byDate || ""} onChange={(e) => setByDate(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button data-testid="goal-save" onClick={save} className="btn-gold flex-1"><Target className="w-4 h-4" /> Set goal</button>
            {m.goal && <button data-testid="goal-clear" onClick={clear} className="btn-ghost text-ruby"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function MilestoneModal({ p, onClose, onDone }) {
  const [draft, setDraft] = useState(null);
  const [marketing, setMarketing] = useState(null);
  const [teaching, setTeaching] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/increase/draft/${p.key}`).then((r) => {
      setDraft(r.data);
      setMarketing({ ...r.data.marketing });
      setTeaching({ ...r.data.teaching });
    });
  }, [p.key]);

  const createBroadcast = async () => {
    setBusy(true);
    try {
      await api.post("/admin/broadcasts", { subject: marketing.subject, body_md: marketing.body_md, audience: "both" });
      toast.success("Draft broadcast created in Daily Heat");
    } catch (e) { toast.error("Could not create broadcast"); } finally { setBusy(false); }
  };
  const captureTeaching = async () => {
    setBusy(true);
    try {
      await api.post("/admin/teachings", { title: teaching.title, body_md: teaching.body_md, metric_key: p.key, metric_label: p.label });
      toast.success("Teaching captured");
    } catch (e) { toast.error("Could not capture teaching"); } finally { setBusy(false); }
  };
  const celebrate = async () => {
    await api.post(`/admin/increase/celebrate/${p.key}`); toast.success("Milestone honored"); onDone();
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-void/80 backdrop-blur flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="panel p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="milestone-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="overline text-gold">// KAIROS MILESTONE — {p.label}</div>
          <button onClick={onClose}><X className="w-5 h-5 text-textMuted hover:text-cream" /></button>
        </div>
        <p className="text-sm text-textMuted mb-6">Reached <span className="text-cream">{p.value}</span> (goal {p.target}). Tell the story to the room, then capture what the season taught you.</p>

        {!draft ? <PanelLoading label="Drafting the templates" /> : (
          <div className="space-y-8">
            {/* Marketing broadcast */}
            <div data-testid="milestone-marketing">
              <div className="overline mb-2 flex items-center gap-2"><Megaphone className="w-4 h-4 text-gold" /> Marketing broadcast (fill in the blanks)</div>
              <input value={marketing.subject} onChange={(e) => setMarketing({ ...marketing, subject: e.target.value })} className="mb-2" />
              <textarea rows={6} value={marketing.body_md} onChange={(e) => setMarketing({ ...marketing, body_md: e.target.value })} />
              <div className="flex gap-2 mt-2">
                <button data-testid="create-broadcast" disabled={busy} onClick={createBroadcast} className="btn-gold text-xs"><Megaphone className="w-3 h-3" /> Create draft broadcast</button>
                <button onClick={() => copy(`${marketing.subject}\n\n${marketing.body_md}`)} className="btn-ghost text-xs"><Copy className="w-3 h-3" /> Copy</button>
              </div>
            </div>

            {/* Teaching capture */}
            <div data-testid="milestone-teaching">
              <div className="overline mb-2 flex items-center gap-2"><Feather className="w-4 h-4 text-gold" /> Capture the teaching</div>
              <input value={teaching.title} onChange={(e) => setTeaching({ ...teaching, title: e.target.value })} className="mb-2" />
              <textarea rows={7} value={teaching.body_md} onChange={(e) => setTeaching({ ...teaching, body_md: e.target.value })} />
              <div className="flex gap-2 mt-2">
                <button data-testid="capture-teaching" disabled={busy} onClick={captureTeaching} className="btn-gold text-xs"><Feather className="w-3 h-3" /> Capture teaching</button>
                <button onClick={() => copy(`${teaching.title}\n\n${teaching.body_md}`)} className="btn-ghost text-xs"><Copy className="w-3 h-3" /> Copy</button>
              </div>
            </div>

            <div className="pt-4 border-t border-borderGold flex items-center justify-between">
              <p className="text-xs text-textDim">Honoring the milestone dismisses this prompt until the goal is raised.</p>
              <button data-testid="celebrate-milestone" onClick={celebrate} className="btn-gold text-xs"><Sparkles className="w-3 h-3" /> Honor & dismiss</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  TEACHINGS — list, publish to Vault as draft article, delete
// ============================================================
function TeachingsView() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/teachings").then((r) => setItems(r.data.teachings)), []);
  useEffect(() => { load(); }, [load]);

  const publish = async (t, vault) => {
    try {
      await api.post(`/admin/teachings/${t.id}/publish`, { vault });
      toast.success(`Sent to ${vault ? "Vault" : "Blog"} as a draft article — review & go live from Articles`);
      load();
    } catch (e) { toast.error("Could not publish"); }
  };
  const del = async (id) => { await api.delete(`/admin/teachings/${id}`); load(); };

  if (!items) return <PanelLoading label="Gathering teachings" />;
  return (
    <div data-testid="teachings-view">
      <div className="overline mb-4">// TEACHINGS CAPTURED — the lessons from each season of increase</div>
      {items.length === 0 ? (
        <p className="text-textMuted">No teachings captured yet. When a goal is reached, capture the lesson from the Kairos milestone prompt.</p>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} data-testid={`teaching-${t.id}`} className="panel p-5">
              <div className="flex items-start gap-3">
                <Feather className="w-5 h-5 text-gold mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl text-cream">{t.title}</div>
                  {t.metric_label && <div className="text-xs text-gold font-mono uppercase tracking-widest mt-1">{t.metric_label}</div>}
                  <p className="text-sm text-textMuted mt-2 line-clamp-3 whitespace-pre-wrap">{t.body_md}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-borderGold">
                {t.published_article_id ? (
                  <span className="text-xs text-emerald-400 font-mono uppercase tracking-widest flex items-center gap-1"><Check className="w-3 h-3" /> Drafted to Articles</span>
                ) : (
                  <>
                    <button data-testid={`publish-vault-${t.id}`} onClick={() => publish(t, true)} className="btn-ghost text-xs"><BookOpen className="w-3 h-3" /> To Vault (draft)</button>
                    <button data-testid={`publish-blog-${t.id}`} onClick={() => publish(t, false)} className="btn-ghost text-xs"><BookOpen className="w-3 h-3" /> To Blog (draft)</button>
                  </>
                )}
                <button onClick={() => del(t.id)} className="btn-ghost text-xs text-ruby ml-auto"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
